import UserSettingsBusinessStore, { IUserSettings } from '@src/service/business/usersettings/userSettingsBusinessStore';
import AppConfigService from '@src/service/common/AppConfigService';
import { ANTDLOCALES } from '@src/service/locale/antd';
import { MESSAGES } from '@src/service/locale/message';
import { CookieManager } from '@src/service/util/CookieManager';
import LocalizeService from '@src/service/util/localize/LocalizeService';
import { ConfigProvider } from 'antd';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';

const defaultLocaleValue = AppConfigService.getValue('app.defaultLocale');
const cookieLocalName = AppConfigService.getValue('cookies.locale.name');
const cookieLocalDuration = new Date(Date.now() + AppConfigService.getValue('cookies.locale.duration'));

const cookieLocalValue = CookieManager.getCookie(cookieLocalName);

// -- Prop types
// ----------

export interface ILdLocaleProviderOwnProps {}

export interface ILdLocaleProviderStateProps {
  userSettings: IUserSettings;
}

export interface ILdLocaleProviderDispatchProps {
  storeLocale: (locale: string) => void;
}

type ILdLocaleProviderProps = ILdLocaleProviderDispatchProps & ILdLocaleProviderOwnProps & ILdLocaleProviderStateProps;

// -- Component
// ----------

/** Component that handles app locale */
class LdLocaleProvider extends React.Component<ILdLocaleProviderProps> {
  componentDidUpdate = (prevProps: ILdLocaleProviderProps) => {
    if (prevProps.userSettings?.locale !== this.props.userSettings?.locale) {
      this.setLocalization(this.props.userSettings.locale);
    }
  };

  componentDidMount = () => {
    // init locale
    if (this.props.userSettings?.locale) {
      this.handleLocalizationInit(this.props.userSettings.locale);
    } else if (cookieLocalValue) {
      this.handleLocalizationInit(cookieLocalValue);
    } else {
      this.handleLocalizationInit(defaultLocaleValue);
    }
  };

  render = () => {
    return (
      <ConfigProvider key={Math.random()} locale={this.getAntdLocale()}>
        {this.props.children}
      </ConfigProvider>
    );
  };

  getAntdLocale = () => {
    if (this.props.userSettings && ANTDLOCALES[this.props.userSettings.locale]) {
      return ANTDLOCALES[this.props.userSettings.locale];
    } else {
      return ANTDLOCALES[defaultLocaleValue];
    }
  };

  setLocalization = (locale: string) => {
    if (MESSAGES[locale]) {
      if (locale !== cookieLocalValue) {
        CookieManager.setCookie({ name: cookieLocalName, value: locale, path: '/', expires: cookieLocalDuration });
      }
      LocalizeService.initLocalize(locale, MESSAGES);
      moment.locale(locale);
    } else {
      console.warn('Unsupported locale, loading default ', defaultLocaleValue);
      CookieManager.setCookie({ name: cookieLocalName, value: defaultLocaleValue, path: '/', expires: cookieLocalDuration });
      LocalizeService.initLocalize(defaultLocaleValue, MESSAGES);
      moment.locale(defaultLocaleValue);
    }
  };

  handleLocalizationInit = (localeValue: string) => {
    this.setLocalization(localeValue);
    this.props.storeLocale(localeValue);
  };
}

// -- HOCs and exports
// ----------

// `state` parameter needs a type annotation to type-check the correct shape of a state object but also it'll be used by "type inference" to infer the type of returned props
const mapStateToProps = (state: any, ownProps: ILdLocaleProviderOwnProps): ILdLocaleProviderStateProps => ({
  userSettings: UserSettingsBusinessStore.selectors.getUserSettings(state),
});

// `dispatch` parameter needs a type annotation to type-check the correct shape of an action object when using dispatch function
const mapDispatchToProps = (dispatch: any): ILdLocaleProviderDispatchProps => ({
  storeLocale: (locale: string) => dispatch(UserSettingsBusinessStore.actions.storeUserSettings({ locale })),
});

export default connect<ILdLocaleProviderStateProps, ILdLocaleProviderDispatchProps, ILdLocaleProviderOwnProps>(mapStateToProps, mapDispatchToProps)(LdLocaleProvider as any);
